import { useState, useEffect, useCallback } from "react";
import { Button, Form, InputNumber, Spin } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import {
  getDiscountCodesPolicy,
  updateDiscountCodesPolicy,
} from "services/discountCodes.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { DISCOUNT_CODES_CONFIG_KEY } from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";
import { CODES_COPY } from "pages/DiscountCodes/commons/discountCodes.copy";
import {
  buildPolicyBody,
  getAllowedCapMax,
  getCapError,
  getShoupCeilingNote,
} from "pages/DiscountCodes/commons/discountCodes.helpers";

/** Organiser's total discount cap (R-32, D9; ui-spec §2.1.6). */
function DiscountCodesSettings() {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, errors } = await getDiscountCodesPolicy();
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setPolicy(data);
    form.setFieldsValue({
      max_total_percent:
        data?.vendor_max_total_percent ?? data?.effective_max_total_percent,
    });
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  // min(platform, ShoUp ceiling) when the view sends it, else platform (D78 R4).
  const maxAllowed = getAllowedCapMax(policy);
  const shoupCeilingNote = getShoupCeilingNote(policy);
  const capOff =
    policy?.cap_set_by_shoup_off === true || policy?.cap_enabled === false;

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    const { data, errors } = await updateDiscountCodesPolicy(
      buildPolicyBody(values)
    );
    setSaving(false);
    if (errors?.length) {
      // CAP_ABOVE_PLATFORM_MAX / CAP_DISABLED: show, then read the policy again.
      renderErrorNotifications(errors);
      load();
      return;
    }
    if (data) setPolicy(data);
    queryClient.invalidateQueries({ queryKey: [DISCOUNT_CODES_CONFIG_KEY] });
    renderSuccessNotifications({
      title: CODES_COPY.settingsToastTitle,
      message: CODES_COPY.settingsToastMessage,
    });
  };

  return (
    <div className="rounded-2xl border border-border/50 bg-card p-6 md:p-8 shadow-card">
      <h3 className="font-bold">{CODES_COPY.settingsHeading}</h3>
      <p className="text-sm text-muted-foreground mt-2">
        {CODES_COPY.settingsText}
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spin />
        </div>
      ) : !policy ? null : capOff ? (
        <p className="text-sm text-muted-foreground mt-4">
          {CODES_COPY.settingsCapOff}
        </p>
      ) : (
        <Form form={form} layout="vertical" className="mt-4">
          {shoupCeilingNote && (
            <p className="text-sm text-muted-foreground mb-4">
              {shoupCeilingNote}
            </p>
          )}
          <Form.Item
            name="max_total_percent"
            label={CODES_COPY.settingsCap}
            extra={CODES_COPY.settingsCapExtra(maxAllowed)}
            rules={[
              {
                validator: (_, value) => {
                  const error = getCapError(value, maxAllowed);
                  return error ? Promise.reject(error) : Promise.resolve();
                },
              },
            ]}
          >
            {/* No min/max on the input: antd would clamp 41 to the max on
                blur and the error would never show (UI-PTL-15). */}
            <InputNumber precision={0} suffix="%" style={{ width: 200 }} />
          </Form.Item>
          <div className="flex justify-end">
            <Button type="primary" loading={saving} onClick={handleSave}>
              {CODES_COPY.settingsBtn}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
}

export default DiscountCodesSettings;
